import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, ChevronRight, Crown, ExternalLink, Handshake, Home, Megaphone,
  MessageCircle, Newspaper, ShieldCheck, UserRound,
} from 'lucide-react';
import SEO from '../components/SEO';
import { fetchUnitAnggota, fetchUnitMemberProfile, getImageUrl } from '../utils/api';

const HEAD_PROFILE = {
  name: 'Ns. Rian Maylina Sari, S.Kep., M.Kep',
  role: 'Ketua Unit Humas & Kerjasama',
  slug: 'ns-rian-maylina-sari-s-kep-m-kep',
  photoId: null,
};

function useMemberProfile(slug, profileType) {
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!slug) {
      setResult(null);
      return undefined;
    }
    let active = true;
    setResult(null);
    fetchUnitMemberProfile(slug, profileType)
      .then((profileResult) => { if (active) setResult(profileResult); })
      .catch(() => undefined);
    return () => { active = false; };
  }, [slug, profileType]);

  return result;
}

function MemberCard({ member }) {
  const profileResult = useMemberProfile(member.profileSlug, member.profileType);
  const photo = profileResult?.profile?.foto
    ? getImageUrl(profileResult.profile.foto, { width: 240, height: 300, quality: 85 })
    : null;
  const profilePath = profileResult && member.profileSlug
    ? `/${profileResult.kind}/${member.profileSlug}`
    : null;

  return (
    <article className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm dark:border-purple-900/30 dark:bg-[#1A182E]">
      <div className="flex items-start gap-4">
        <div className="grid h-20 w-16 shrink-0 place-items-center overflow-hidden rounded-xl bg-purple-100 dark:bg-purple-900/30">
          {photo ? <img src={photo} alt={member.name} loading="lazy" className="h-full w-full object-cover" /> : <span className="text-lg font-bold text-[#7B2FBE]">{member.name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()}</span>}
        </div>
        <div className="min-w-0 flex-1">
          {member.isHead && <span className="mb-1.5 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-400 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-amber-950 shadow-sm dark:border-amber-300/40 dark:bg-amber-300 dark:text-amber-950"><Crown size={10} /> Kepala Unit</span>}
          <h3 className="font-heading text-sm font-bold leading-tight text-[#1A1A2E] dark:text-white">{member.name}</h3>
          <p className="mt-0.5 text-xs text-[#7B2FBE] dark:text-purple-300">{member.position}</p>
          {member.tasks?.length > 0 && <ul className="mt-3 space-y-1.5">{member.tasks.map((task, index) => <li key={index} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400"><ArrowRight size={12} className="mt-0.5 shrink-0 text-[#7B2FBE]" />{task}</li>)}</ul>}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {profilePath && <Link to={profilePath} className="inline-flex items-center gap-1.5 text-xs text-[#7B2FBE] hover:underline"><UserRound size={12} /> Lihat profil</Link>}
            {member.whatsapp && <a href={`https://wa.me/${member.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:underline dark:text-emerald-400"><MessageCircle size={12} /> Hubungi via WhatsApp</a>}
          </div>
        </div>
      </div>
    </article>
  );
}

const services = [
  {
    icon: Newspaper,
    title: 'Informasi & Publikasi',
    description: 'Menyampaikan informasi resmi, berita, dan publikasi kegiatan institusi kepada masyarakat.',
  },
  {
    icon: Megaphone,
    title: 'Hubungan Masyarakat',
    description: 'Membangun komunikasi yang terbuka dan positif antara kampus, sivitas akademika, dan publik.',
  },
  {
    icon: Handshake,
    title: 'Kemitraan & Kerjasama',
    description: 'Mendukung hubungan kelembagaan dan pengembangan kerja sama yang bermanfaat bagi institusi.',
  },
];

const Humas = () => {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    let active = true;
    fetchUnitAnggota('humas')
      .then((items) => { if (active) setMembers(Array.isArray(items) ? items : []); })
      .catch(() => { if (active) setMembers([]); });
    return () => { active = false; };
  }, []);

  const configuredHead = members.find((member) => member.isHead) || members[0] || null;
  const headProfile = configuredHead
    ? {
      name: configuredHead.name,
      role: configuredHead.position,
      slug: configuredHead.profileSlug,
      profileType: configuredHead.profileType,
      photoId: null,
      whatsapp: configuredHead.whatsapp,
    }
    : HEAD_PROFILE;
  const headProfileResult = useMemberProfile(headProfile.slug, headProfile.profileType || (configuredHead ? 'tendik' : 'dosen'));
  const profilePhoto = headProfileResult?.profile?.foto || headProfile.photoId ? getImageUrl(headProfileResult?.profile?.foto || headProfile.photoId, {
    width: 720,
    height: 900,
    quality: 88,
  }) : null;
  const profilePath = headProfile.slug && headProfileResult
    ? `/${headProfileResult.kind}/${headProfile.slug}`
    : !configuredHead && headProfile.slug ? `/dosen/${headProfile.slug}` : null;

  return (
  <div data-testid="humas-page">
    <SEO
      title="Humas & Kerjasama"
      description="Unit Humas dan Kerjasama STIKES Harapan Ibu Jambi serta akses layanan informasi publik melalui PPID."
      image={profilePhoto}
    />

    <div className="page-hero pt-[70px]">
      <div className="relative z-10 mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-white/70">
            <Link to="/" className="flex items-center gap-1 transition-colors hover:text-white">
              <Home size={14} /> Beranda
            </Link>
            <ChevronRight size={14} />
            <span>Tentang Kami</span>
            <ChevronRight size={14} />
            <span className="text-white">Humas</span>
          </div>
          <h1 className="font-heading text-4xl font-bold text-white md:text-5xl">Humas & Kerjasama</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80">
            Penghubung informasi, komunikasi, dan kemitraan STIKES Harapan Ibu Jambi dengan sivitas akademika dan masyarakat.
          </p>
        </motion.div>
      </div>
    </div>

    <main className="bg-[#F8F7FF] dark:bg-[#121020]">
      <section className="py-16">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-[360px_minmax(0,1fr)] lg:px-8">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-hidden rounded-3xl border border-purple-100 bg-white shadow-sm dark:border-purple-900/30 dark:bg-[#1A182E]"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-purple-100 dark:bg-purple-950/30">
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt={headProfile.name}
                  className="h-full w-full object-cover object-top"
                />
              ) : (
                <div className="grid h-full w-full place-items-center bg-gradient-to-br from-[#7B2FBE] to-[#4C1D95] text-6xl font-bold text-white">
                  {headProfile.name.split(' ').filter(Boolean).slice(0, 2).map(word => word[0]).join('')}
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#171128]/90 to-transparent" />
              <span className="absolute bottom-4 left-4 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-400 px-3 py-1.5 text-xs font-extrabold text-amber-950 shadow-lg shadow-black/20">
                <Crown size={13} /> Kepala Unit
              </span>
            </div>
            <div className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7B2FBE] dark:text-purple-300">
                Pimpinan Humas
              </p>
              <h2 className="mt-2 font-heading text-xl font-bold leading-snug text-[#1A1A2E] dark:text-white">
                {headProfile.name}
              </h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{headProfile.role}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                {profilePath && <Link to={profilePath} className="inline-flex items-center gap-2 rounded-xl border border-purple-200 px-4 py-2.5 text-sm font-semibold text-[#7B2FBE] transition hover:border-[#7B2FBE] hover:bg-purple-50 dark:border-purple-800 dark:text-purple-300 dark:hover:bg-purple-900/20"><UserRound size={16} /> Lihat Profil</Link>}
                {headProfile.whatsapp && <a href={`https://wa.me/${headProfile.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 px-4 py-2.5 text-sm font-semibold text-emerald-600 transition hover:bg-emerald-50 dark:border-emerald-800/50 dark:text-emerald-400 dark:hover:bg-emerald-900/20"><MessageCircle size={16} /> Hubungi via WhatsApp</a>}
              </div>
            </div>
          </motion.article>

          <div className="flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7B2FBE] dark:text-purple-300">
                Unit & Lembaga
              </span>
              <h2 className="mt-3 font-heading text-3xl font-bold text-[#1A1A2E] dark:text-white">
                Unit Humas & Kerjasama
              </h2>
              <p className="mt-5 leading-relaxed text-gray-600 dark:text-gray-300">
                Unit Humas & Kerjasama berperan sebagai pusat komunikasi institusi dalam menyampaikan informasi resmi dan membangun hubungan yang baik dengan masyarakat, media, mitra, serta seluruh sivitas akademika STIKES Harapan Ibu Jambi.
              </p>
              <p className="mt-4 leading-relaxed text-gray-600 dark:text-gray-300">
                Melalui komunikasi yang informatif, transparan, dan kolaboratif, unit ini mendukung penguatan citra institusi sekaligus perluasan jejaring kerja sama.
              </p>
            </motion.div>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {services.map(({ icon: Icon, title, description }, index) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + index * 0.08 }}
                  className="rounded-2xl border border-purple-100 bg-white p-5 dark:border-purple-900/30 dark:bg-[#1A182E]"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-purple-100 text-[#7B2FBE] dark:bg-purple-900/30 dark:text-purple-300">
                    <Icon size={19} />
                  </span>
                  <h3 className="mt-4 font-heading text-sm font-bold text-[#1A1A2E] dark:text-white">{title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">{description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {members.length > 1 && (
        <section className="bg-[#F8F7FF] py-16 dark:bg-[#121020]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-7"><span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7B2FBE] dark:text-purple-300">Tim Humas</span><h2 className="mt-3 font-heading text-3xl font-bold text-[#1A1A2E] dark:text-white">Anggota Unit Humas & Kerjasama</h2></div>
            <div className="grid gap-5 md:grid-cols-2">{members.filter((member) => member.id !== configuredHead?.id).map((member) => <MemberCard key={member.id} member={member} />)}</div>
          </div>
        </section>
      )}

      <section className="bg-white py-16 dark:bg-[#100E1C]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#4C1D95] via-[#7B2FBE] to-[#9333EA] p-7 text-white shadow-xl shadow-purple-900/10 sm:p-10"
          >
            <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10" />
            <div className="absolute -bottom-24 right-32 h-48 w-48 rounded-full bg-white/5" />
            <div className="relative z-10 flex flex-col gap-7 md:flex-row md:items-center md:justify-between">
              <div className="max-w-3xl">
                <div className="flex items-center gap-2 text-sm font-semibold text-purple-100">
                  <ShieldCheck size={19} /> Transparansi Informasi Publik
                </div>
                <h2 className="mt-3 font-heading text-2xl font-bold sm:text-3xl">
                  Pejabat Pengelola Informasi dan Dokumentasi
                </h2>
                <p className="mt-3 leading-relaxed text-purple-100">
                  Akses informasi publik, dokumen, serta layanan permohonan informasi STIKES Harapan Ibu Jambi melalui portal resmi PPID.
                </p>
              </div>
              <a
                href="https://ppid.stikes-hi.ac.id"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-[#6D28A8] shadow-lg transition hover:-translate-y-0.5 hover:bg-purple-50"
              >
                Kunjungi Portal PPID <ExternalLink size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  </div>
  );
};

export default Humas;
